import _ from 'lodash';

import { adminClient as apolloClient } from '@/store/apollo';

import ModelsQuery from '@/extensions/generative-media/store/graphql/queries/GenerativeMediaModels.gql';
import IncurredCostQuery from '@/extensions/generative-media/store/graphql/queries/GenerativeMediaIncurredCost.gql';
import CostCeilingQuery from '@/extensions/generative-media/store/graphql/queries/GenerativeMediaCostCeiling.gql';
import ActiveTaskQuery from '@/extensions/generative-media/store/graphql/queries/ActiveGenerativeMediaTask.gql';
import SettingsQuery from '@/extensions/generative-media/store/graphql/queries/CmsGenerativeMediaSettings.gql';
import FetchEditableImageQuery from '@/extensions/generative-media/store/graphql/queries/FetchEditableImage.gql';
import SaveSettingsMutation from '@/extensions/generative-media/store/graphql/mutations/SaveCmsGenerativeMediaSettings.gql';
import SuggestMutation from '@/extensions/generative-media/store/graphql/mutations/SuggestGenerativeMediaOptions.gql';
import PhrasePromptMutation from '@/extensions/generative-media/store/graphql/mutations/PhraseGenerativeMediaPrompt.gql';
import GenerateVideoMutation from '@/extensions/generative-media/store/graphql/mutations/GenerateProductVideo.gql';
import GenerateImageEditMutation from '@/extensions/generative-media/store/graphql/mutations/GenerateImageEdit.gql';
import PromptTemplatesQuery from '@/extensions/generative-media/store/graphql/queries/PromptTemplates.gql';
import PromptTemplatesPaginatedQuery from '@/extensions/generative-media/store/graphql/queries/PromptTemplatesPaginated.gql';
import SavePromptTemplateMutation from '@/extensions/generative-media/store/graphql/mutations/SavePromptTemplate.gql';
import DeletePromptTemplateMutation from '@/extensions/generative-media/store/graphql/mutations/DeletePromptTemplate.gql';
import PhraseTemplatePromptMutation from '@/extensions/generative-media/store/graphql/mutations/PhraseTemplatePrompt.gql';
import TestPromptTemplateMutation from '@/extensions/generative-media/store/graphql/mutations/TestPromptTemplate.gql';
import ApplyImageCropMutation from '@/extensions/generative-media/store/graphql/mutations/ApplyImageCrop.gql';
import SetActiveImageVariantMutation from '@/extensions/generative-media/store/graphql/mutations/SetActiveImageVariant.gql';
import DeleteImageVariantMutation from '@/extensions/generative-media/store/graphql/mutations/DeleteImageVariant.gql';

export default {
    fetchModels: (capability, cmsId) =>
        apolloClient.query({
            query: ModelsQuery,
            // GraphQL returns ids as strings; the arg is Int — coerce here.
            variables: { capability, cms_id: parseInt(cmsId, 10) },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_Models),

    fetchSettings: (cmsId) =>
        apolloClient.query({
            query: SettingsQuery,
            variables: { cms_id: parseInt(cmsId, 10) },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_CmsSettings),

    incurredCost: (cmsId, capability) =>
        apolloClient.query({
            query: IncurredCostQuery,
            variables: { cms_id: parseInt(cmsId, 10), capability },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_IncurredCost),

    // The ceiling replaces the flat effectivePrice x multiplier estimate once the
    // levers are known: at most this amount, given the actual quality/resolution
    // chosen — never an exact quote (fal's own price varies with prompt complexity).
    costCeiling: (cmsId, modelKey, quantity, levers = {}, referenceCount = 0) =>
        apolloClient.query({
            query: CostCeilingQuery,
            variables: {
                cms_id: parseInt(cmsId, 10),
                model_key: modelKey,
                quantity,
                quality: levers.quality || null,
                resolution: levers.resolution || null,
                resolution_cap: levers.resolution_cap || null,
                reference_count: referenceCount,
            },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_CostCeiling),

    activeTask: (productId, imageId, uploadId, fileId, capability = null) =>
        apolloClient.query({
            query: ActiveTaskQuery,
            variables: { product_id: productId ? parseInt(productId, 10) : null, image_id: imageId ? parseInt(imageId, 10) : null, upload_id: uploadId ? parseInt(uploadId, 10) : null, file_id: fileId ? parseInt(fileId, 10) : null, capability },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_ActiveTask),

    // Fetch a single Image with its full variant stack — used to adopt a freshly-promoted
    // Image after a fresh-upload AI edit (the upload became a real Image server-side).
    fetchEditableImage: (id) =>
        apolloClient.query({
            query: FetchEditableImageQuery,
            variables: { id },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.Image),

    saveSettings: (cmsId, input) =>
        apolloClient.mutate({
            mutation: SaveSettingsMutation,
            variables: { cms_id: parseInt(cmsId, 10), input },
        }).then(({ data }) => data.EX_GM_SaveCmsSettings),

    suggestOptions: (productId, capability, imageIds, locale, exclude, uploadIds, cmsId, fileIds, articleId) =>
        apolloClient.mutate({
            mutation: SuggestMutation,
            variables: { product_id: productId ? parseInt(productId, 10) : null, article_id: articleId ? parseInt(articleId, 10) : null, cms_id: cmsId ? parseInt(cmsId, 10) : null, capability, image_ids: imageIds, upload_ids: uploadIds, file_ids: fileIds, locale, exclude },
        }).then(({ data }) => data.EX_GM_SuggestOptions),

    phrasePrompt: (variables) =>
        apolloClient.mutate({
            mutation: PhrasePromptMutation,
            variables,
        }).then(({ data }) => data.EX_GM_PhrasePrompt),

    generateVideo: (variables) =>
        apolloClient.mutate({
            mutation: GenerateVideoMutation,
            variables,
        }).then(({ data }) => data.EX_GM_GenerateProductVideo),

    generateImageEdit: (variables) =>
        apolloClient.mutate({
            mutation: GenerateImageEditMutation,
            variables,
        }).then(({ data }) => data.EX_GM_GenerateImageEdit),

    applyImageCrop: (variables) =>
        apolloClient.mutate({
            mutation: ApplyImageCropMutation,
            variables,
        }).then(({ data }) => data.EX_GM_ApplyImageCrop),

    setActiveImageVariant: (imageId, variantId) =>
        apolloClient.mutate({
            mutation: SetActiveImageVariantMutation,
            variables: { image_id: imageId, variant_id: variantId },
        }).then(({ data }) => data.EX_GM_SetActiveImageVariant),

    deleteImageVariant: (imageId, variantId) =>
        apolloClient.mutate({
            mutation: DeleteImageVariantMutation,
            variables: { image_id: imageId, variant_id: variantId },
        }).then(({ data }) => data.EX_GM_DeleteImageVariant),


    // ===== Prompt templates =====

    fetchPromptTemplates: (cmsId, limit = 5, offset = 0, context = null) =>
        apolloClient.query({
            query: PromptTemplatesQuery,
            // GraphQL returns ids as strings; the args are Int — coerce here.
            variables: { cms_id: parseInt(cmsId, 10), limit, offset, context },
            fetchPolicy: 'no-cache',
        }).then(({ data }) => data.EX_GM_PromptTemplates),

    fetchPromptTemplatesPaginated: (data, cmsId) =>
        apolloClient.query({
            query: PromptTemplatesPaginatedQuery,
            variables: {
                cms_id: parseInt(cmsId, 10),
                currentPage: _.get(data, 'pageInfo.currentPage', 1),
                perPage: _.get(data, 'pageInfo.perPage', 25),
                order: _.get(data, 'variables.order', 'created_at'),
                direction: _.get(data, 'variables.direction', 'desc'),
                filters: _.map(data.filters, entry => entry),
            },
            fetchPolicy: 'network-only',
        }).then(({ data: result }) => result.EX_GM_PromptTemplatesPaginated),

    savePromptTemplate: (cmsId, template) =>
        apolloClient.mutate({
            mutation: SavePromptTemplateMutation,
            variables: { cms_id: parseInt(cmsId, 10), template },
        }).then(({ data }) => data.EX_GM_SavePromptTemplate),

    deletePromptTemplate: (id) =>
        apolloClient.mutate({
            mutation: DeletePromptTemplateMutation,
            variables: { id: parseInt(id, 10) },
        }).then(({ data }) => data.EX_GM_DeletePromptTemplate),

    phraseTemplatePrompt: (variables) =>
        apolloClient.mutate({
            mutation: PhraseTemplatePromptMutation,
            // GraphQL returns ids as strings; the arg is Int — coerce here.
            variables: { ...variables, cms_id: parseInt(variables.cms_id, 10) },
        }).then(({ data }) => data.EX_GM_PhraseTemplatePrompt),

    testPromptTemplate: (variables) =>
        apolloClient.mutate({
            mutation: TestPromptTemplateMutation,
            variables: { ...variables, template_id: parseInt(variables.template_id, 10) },
        }).then(({ data }) => data.EX_GM_TestPromptTemplate),

    // "Don't keep asking" flag for the delete-layer dialog — same localStorage
    // convention as FileApi.noDeleteDialog (key `no-delete-dialog:{type}`).
    noDeleteVariantDialog: (value) => {
        if (value !== undefined) {
            localStorage.setItem('no-delete-dialog:image-variant', value);
        } else {
            return localStorage.getItem('no-delete-dialog:image-variant');
        }
    },
};
